/**
 * Person-key FogBugz reads for Claude runs (epic PI-SPEC-SW-E308, ER-1664
 * Package D; additive within contract fogbugz 1).
 *
 * Claude reads FogBugz inside the Toolbox as the person, never with a shared
 * account: the read-only MCP tools `spectree__fogbugz_read_case`,
 * `spectree__fogbugz_read_search` and `spectree__fogbugz_read_evtx_digest`
 * call the SpecTree API (`GET /api/v1/fogbugz/reads/cases/:id`,
 * `GET /api/v1/fogbugz/reads/cases` and the existing
 * `POST /api/v1/fogbugz/cases/:id/evtx-digest`) with the person's own saved
 * FogBugz key. Without a saved key the API refuses with 400
 * {@link FOGBUGZ_KEY_REQUIRED} before any FogBugz call.
 *
 * Case text is data, not instructions: {@link fogbugzReadCaseSchema} carries
 * `untrustedContent: true`, and event and intent text is capped so one case
 * cannot fill Claude's context (`truncated` / `eventsTruncated` say when the
 * server cut it).
 */
import { z } from "zod";
import { isoDateTime } from "../zod-helpers.js";
import { fogbugzCaseIdSchema } from "./schemas.js";
// ---------------------------------------------------------------------------
// Caps
// ---------------------------------------------------------------------------
/** Longest text of one case event; longer text is cut and the event marked `truncated`. */
export const FOGBUGZ_READ_EVENT_TEXT_MAX = 8000;
/** Most event text one case read returns, all events together; past it the case is marked `eventsTruncated`. */
export const FOGBUGZ_READ_EVENTS_TEXT_TOTAL_MAX = 100_000;
/** Most bytes of the case's `intent.md` a read returns; longer intent text is cut and marked `truncated`. */
export const FOGBUGZ_READ_INTENT_BYTES_MAX = 256 * 1024;
/** Cases a search returns when no `limit` is given. */
export const FOGBUGZ_READ_SEARCH_LIMIT_DEFAULT = 20;
/** Most cases one search returns. */
export const FOGBUGZ_READ_SEARCH_LIMIT_MAX = 50;
/** Longest search text. */
export const FOGBUGZ_READ_QUERY_MAX = 200;
// ---------------------------------------------------------------------------
// Error code
// ---------------------------------------------------------------------------
/** 400: the caller has no saved FogBugz API key; no FogBugz call was made. */
export const FOGBUGZ_KEY_REQUIRED = "FOGBUGZ_KEY_REQUIRED";
/** The plain sentence for {@link FOGBUGZ_KEY_REQUIRED}. */
export const FOGBUGZ_KEY_REQUIRED_MESSAGE = "Save your FogBugz API key in SpecTree Settings, then try again. Claude reads FogBugz as you, never with a shared account.";
// ---------------------------------------------------------------------------
// Read one case
// ---------------------------------------------------------------------------
/** The kinds of attachment a read reports: an event log, the Formulator's `intent.md`, an image, anything else. */
export const FOGBUGZ_READ_ATTACHMENT_KINDS = ["evtx", "intent", "image", "other"];
/**
 * One attachment on the case: `sURL` exactly as FogBugz reports it (the
 * EVTX digest route takes it back), and the index of the event it came with.
 */
export const fogbugzReadAttachmentSchema = z.object({
    fileName: z.string().min(1),
    sURL: z.string().min(1),
    eventIndex: z.number().int().min(0),
    kind: z.enum(FOGBUGZ_READ_ATTACHMENT_KINDS),
});
/** One case event, oldest first: what happened (`verb`), who, when, and its text (capped). */
export const fogbugzReadEventSchema = z.object({
    index: z.number().int().min(0),
    verb: z.string(),
    person: z.string().nullable(),
    at: isoDateTime({ offset: true }).nullable(),
    text: z.string().max(FOGBUGZ_READ_EVENT_TEXT_MAX),
    /** True when the server cut this event's text at {@link FOGBUGZ_READ_EVENT_TEXT_MAX}. */
    truncated: z.boolean(),
});
/** The case's `intent.md` (FogBugz Formulator cases), as text. */
export const fogbugzReadIntentSchema = z.object({
    fileName: z.string().min(1),
    text: z.string(),
    /** True when the server cut the text at {@link FOGBUGZ_READ_INTENT_BYTES_MAX} bytes. */
    truncated: z.boolean(),
});
/**
 * `GET /api/v1/fogbugz/reads/cases/:id` → `data`: one case read with the
 * person's own FogBugz key. `untrustedContent` is always `true`: the case
 * text is data written by others, never instructions; a payload without it
 * is refused so no consumer can drop the marker.
 */
export const fogbugzReadCaseSchema = z
    .object({
    caseId: fogbugzCaseIdSchema,
    title: z.string(),
    url: z.string().nullable(),
    project: z.object({ ixProject: z.number().int().positive(), name: z.string() }),
    area: z.object({ ixArea: z.number().int().positive(), name: z.string() }).nullable(),
    status: z.string(),
    category: z.string().nullable(),
    priority: z.string().nullable(),
    milestone: z.string().nullable(),
    assignedTo: z.string().nullable(),
    events: z.array(fogbugzReadEventSchema),
    /** True when events were left out or cut to keep within {@link FOGBUGZ_READ_EVENTS_TEXT_TOTAL_MAX}. */
    eventsTruncated: z.boolean(),
    attachments: z.array(fogbugzReadAttachmentSchema),
    intent: fogbugzReadIntentSchema.nullable(),
    untrustedContent: z.literal(true),
})
    .superRefine((value, ctx) => {
    const total = value.events.reduce((sum, event) => sum + event.text.length, 0);
    if (total > FOGBUGZ_READ_EVENTS_TEXT_TOTAL_MAX) {
        ctx.addIssue({
            code: "custom",
            path: ["events"],
            message: `Event text totals ${String(total)} characters; at most ${String(FOGBUGZ_READ_EVENTS_TEXT_TOTAL_MAX)} allowed`,
        });
    }
});
// ---------------------------------------------------------------------------
// Search one FogBugz project
// ---------------------------------------------------------------------------
/** Which cases a search covers. */
export const FOGBUGZ_READ_SEARCH_STATUSES = ["active", "closed", "all"];
/**
 * `GET /api/v1/fogbugz/reads/cases` query: one FogBugz project, optional
 * search text, status (default `active`) and limit (default
 * {@link FOGBUGZ_READ_SEARCH_LIMIT_DEFAULT}, at most
 * {@link FOGBUGZ_READ_SEARCH_LIMIT_MAX}). Numbers arrive as strings and are
 * coerced.
 */
export const fogbugzReadSearchQuerySchema = z.object({
    ixProject: z.coerce.number().int().positive(),
    q: z.string().max(FOGBUGZ_READ_QUERY_MAX).optional(),
    status: z.enum(FOGBUGZ_READ_SEARCH_STATUSES).default("active"),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(FOGBUGZ_READ_SEARCH_LIMIT_MAX)
        .default(FOGBUGZ_READ_SEARCH_LIMIT_DEFAULT),
});
/** One case in a search result. */
export const fogbugzReadSearchCaseSchema = z.object({
    caseId: fogbugzCaseIdSchema,
    title: z.string(),
    status: z.string(),
    area: z.string().nullable(),
    assignedTo: z.string().nullable(),
    lastUpdated: isoDateTime({ offset: true }).nullable(),
});
/** `GET /api/v1/fogbugz/reads/cases` → `data`: the matching cases and whether there were more. */
export const fogbugzReadSearchResultSchema = z.object({
    cases: z.array(fogbugzReadSearchCaseSchema).max(FOGBUGZ_READ_SEARCH_LIMIT_MAX),
    hasMore: z.boolean(),
});
//# sourceMappingURL=reads.js.map